/**
 * DiagnosticoSpin — captura do diagnostico SPIN Selling (S/P/I/N).
 *
 * 4 cartoes (Situacao, Problema, Implicacao, Necessidade). Cada um:
 *   - mostra o roteiro de perguntas sugeridas (hard-coded, adaptado a moveis);
 *   - lista as notas daquela categoria (do `spin` agrupado do detalhe);
 *   - textarea + botao "adicionar" que cria uma LeadNota categorizada.
 * Cabecalho do bloco traz o SpinHealth (anel de completude). Excluir nota
 * inline (X). Editar fica para as notas gerais (bloco proprio) — aqui o foco
 * eh capturar respostas rapido.
 *
 * `podeEditar` (lead:edit) governa adicionar/excluir: as notas do SPIN sao
 * LeadNota, e escrever nelas exige lead:edit no backend (CRM-23). Sem a
 * permissao o bloco e so leitura.
 */
import React, { useState } from "react";
import { FiTrash2 } from "react-icons/fi";

import Block from "../../../components/Block";
import AsyncButton from "../../../components/AsyncButton";
import { SPIN_ESTAGIOS } from "../../../services/crm";
import { tempoRel } from "../crmHelpers";

import SpinHealth from "./SpinHealth";
import {
  MiniButton,
  NotaItem,
  NotaMeta,
  NotaTexto,
  SpinCard,
  SpinCardHead,
  SpinCardTitle,
  SpinEstagioGrid,
  SpinRoteiro,
} from "./styles";
import { Textarea } from "../styles";

function EstagioCard({ estagio, notas, onAdicionar, onExcluir, podeEditar }) {
  const [texto, setTexto] = useState("");
  const preenchido = notas.length > 0;

  const adicionar = async () => {
    const t = texto.trim();
    if (!t) return; // botao fica desabilitado; guarda defensiva
    await onAdicionar(estagio.key, t);
    setTexto("");
  };

  return (
    <SpinCard $preenchido={preenchido}>
      <SpinCardHead>
        <SpinCardTitle>{estagio.label}</SpinCardTitle>
        <span className={`badge ${preenchido ? "success" : "neutral"}`}>
          {notas.length} nota{notas.length === 1 ? "" : "s"}
        </span>
      </SpinCardHead>

      <SpinRoteiro>{estagio.roteiro}</SpinRoteiro>

      {notas.map((n) => (
        <NotaItem key={n.id}>
          <NotaTexto>{n.texto}</NotaTexto>
          <NotaMeta>
            <span>{n.autor_nome || "—"}</span>
            <span title={n.created_at}>· {tempoRel(n.created_at)}</span>
            {podeEditar && (
              <MiniButton
                type="button"
                $tone="danger"
                onClick={() => onExcluir(n.id)}
                title="Excluir nota"
                style={{ marginLeft: "auto" }}
              >
                <FiTrash2 size={12} aria-hidden="true" />
              </MiniButton>
            )}
          </NotaMeta>
        </NotaItem>
      ))}

      {podeEditar && (
        <>
          <Textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={2}
            placeholder={`Registre a resposta do cliente (${estagio.descricao})`}
          />
          <AsyncButton
            variant="secondary"
            onClick={adicionar}
            successMessage="Nota adicionada."
            silentError
            disabled={!texto.trim()}
            style={{ alignSelf: "flex-start" }}
          >
            Adicionar
          </AsyncButton>
        </>
      )}
    </SpinCard>
  );
}

function DiagnosticoSpin({
  spin,
  completude,
  onAdicionarNota,
  onExcluirNota,
  podeEditar = false,
  collapsible,
  defaultCollapsed,
}) {
  const agrupado = spin || {};
  const total = completude?.total ?? 0;

  return (
    <Block
      title="Diagnóstico SPIN"
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
      actions={
        <SpinHealth
          total={total}
          label={`${total}/4 estágios cobertos`}
        />
      }
    >
      <SpinEstagioGrid>
        {SPIN_ESTAGIOS.map((estagio) => (
          <EstagioCard
            key={estagio.key}
            estagio={estagio}
            notas={Array.isArray(agrupado[estagio.key]) ? agrupado[estagio.key] : []}
            onAdicionar={onAdicionarNota}
            onExcluir={onExcluirNota}
            podeEditar={podeEditar}
          />
        ))}
      </SpinEstagioGrid>
    </Block>
  );
}

export default DiagnosticoSpin;
